import type { Signal } from '@preact/signals';
import { type ComponentChildren, createContext } from 'preact';
import { useContext } from 'preact/hooks';
import type { CategoryData } from 'service/category/model.ts';
import type { WatchdogsSignal } from 'signals/watchdog.ts';
import type { FilterSignal } from 'signals/filters';
import type { OffersList } from 'utils/isOfferType';
import type { BazarCategoryTree, BazarCategoryTreeCategory } from 'service/category/utils.ts';

type SignalsContext = {
	watchdogs: WatchdogsSignal;
	watchdogsLoading: Signal<boolean>;
	categoriesData: Signal<CategoryData[]>;
	filters: FilterSignal;
	offers: Signal<{ response: OffersList; isLoading?: boolean; isAppend?: boolean }>;
	displayCategories: Signal<CategoryData[]>;
	phraseCategories: Signal<CategoryData[]>;
	offerCountsOverride: Signal<Map<number, number>>;
	tabShowSavedOffers: Signal<boolean>;
	isQueryOffersLoading: Signal<boolean>;

	// BazarCategoryList signals
	// todo: due to the fact that this signal is used in multiple places, the types are not perfect. Homepage is the only page that requires searchToken to be present in the category, and it is guaranteed for that page
	activeCategory: Signal<BazarCategoryTreeCategory | null>;
	availableCategories: Signal<BazarCategoryTree>;
	categoryCountsOverride: Signal<Record<number, number>>;
	expandedCategories: Signal<Record<number, boolean>>;
};

// Make the context partial so that we can create a provider without all the signals
export const SignalsContext = createContext<Partial<SignalsContext>>({});

/**
 *
 * @param signals declaration of what signals are expected to be used. The hook will check for you that they are present in the context.
 */
export const useSignals = (signals?: (keyof SignalsContext)[]) => {
	// Remove the `Partial` from the context to remove null checks for context. To make sure the context is populated we have the debug check below
	const ctx = useContext(SignalsContext) as SignalsContext;

	// #if [DEBUG_LOCAL=1]
	// Check that all signals expected to be used are present in the context
	if (signals?.length) {
		const missingSignals = [];
		for (const signal of signals) {
			if (ctx[signal] === undefined) {
				missingSignals.push(signal);
			}
		}
		if (missingSignals.length) {
			throw new Error(`Missing signals: ${missingSignals.join(', ')}`);
		}
	}
	// #endif

	return ctx;
};

export const SignalsProvider = ({
	children,
	value,
}: {
	children: ComponentChildren;
	value: Partial<SignalsContext>;
}) => {
	return <SignalsContext.Provider value={value}>{children}</SignalsContext.Provider>;
};
